"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ArrowRight, Globe, ShieldCheck, TrendingUp } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const SuccessJurisdictions = () => {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll();
  return <section id="SuccessJurisdictions" className="hidden relative w-full py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {}
          <div ref={textRef} className={cn("lg:col-span-4 flex flex-col justify-center space-y-8 transition-all duration-1000 ease-out", isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-medium text-primary tracking-tight mb-6">
                Global Success & <br />
                <span className="italic text-muted-foreground">
                  Strategic Jurisdictions
                </span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Expat Legacy aligns your residency, business activities, and
                wealth planning into a coherent international framework. We
                navigate the complexities of cross-border regulations to build
                solid foundations for your freedom.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="mt-1 p-2 rounded-full bg-accent/30 text-primary">
                  <Globe className="size-5" />
                </div>
                <div>
                  <h3 className="text-primary font-medium text-lg">
                    Global Mobility
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Seamless residency transitions across premier jurisdictions.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2 rounded-full bg-accent/30 text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="text-primary font-medium text-lg">
                    Compliance & Security
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Rigorous adherence to international legal standards.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2 rounded-full bg-accent/30 text-primary">
                  <TrendingUp className="size-5" />
                </div>
                <div>
                  <h3 className="text-primary font-medium text-lg">
                    Wealth Optimization
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sustainable structures for long-term asset protection.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 bg-card/50 p-8 md:p-12 rounded-sm border border-border/40 shadow-sm backdrop-blur-sm">
            <div className="mb-8">
              <h3 className="text-2xl font-serif text-primary mb-2">
                Start Your Assessment
              </h3>
              <p className="text-muted-foreground text-sm">
                Tell us about your goals. Our team will analyze your profile for
                optimal jurisdictional alignment.
              </p>
            </div>

            <FormProvider formId="contact-form-success-jurisdictions" sectionName="success-jurisdictions" defaultValues={{
            name: "",
            revenue: "",
            destination: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            revenue: {
              required: true
            },
            destination: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Request Consultation" successMessage="Your inquiry has been received. A specialist will contact you shortly.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
};
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
          <ShieldCheck className="size-8 text-accent" />
        </div>
        <h3 className="text-2xl font-serif text-primary">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const minimalInputStyles = "border-0 border-b border-input rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-accent bg-transparent shadow-none transition-colors duration-300 placeholder:text-muted-foreground/60 h-auto text-base";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Sébastien Villa" {...field} className={minimalInputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="revenue" rules={{
        required: "Annual revenue is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Annual Revenue (USD)
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. $1M - $5M" {...field} className={minimalInputStyles} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="destination" rules={{
      required: "Please select a target destination"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Target Destination
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={cn(minimalInputStyles, "w-full flex justify-between items-center")}>
                  <SelectValue placeholder="Select a jurisdiction preference" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="dubai">Dubai (UAE)</SelectItem>
                <SelectItem value="singapore">Singapore</SelectItem>
                <SelectItem value="switzerland">Switzerland</SelectItem>
                <SelectItem value="caribbean">Caribbean (Citizenship)</SelectItem>
                <SelectItem value="europe">European Union</SelectItem>
                <SelectItem value="undecided">Undecided / Need Advice</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please describe your situation"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your current structure and goals..." {...field} className={cn(minimalInputStyles, "min-h-[100px] resize-none")} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm text-base font-medium tracking-wide uppercase transition-all shadow-md hover:shadow-lg">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Processing...
            </> : <>
              Submit Assessment <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default SuccessJurisdictions;